import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IconContrast,
    IconLayoutAlignBottom,
    IconLayoutAlignMiddle,
    IconLayoutAlignTop,
    IconPalette,
    IconPrinter,
    IconRectangle,
    IconRectangleVertical,
    IconRefresh,
    IconRestore,
    IconRotateClockwise,
    IconSparkles,
    IconWand,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import PhotoBoothInbox from '../../../../../components/common/PhotoBoothInbox';
import NidCardCropModal from '../../components/image/NidCardCropModal';
import useImagePaste from '../../components/image/useImagePaste';
import Hint from '../../../../../components/common/Hint';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolCard from '../../components/common/ToolCard';
import NidLivePreview from './NidLivePreview';
import {
    createNidPageImage, createNidPdf, isSupportedImageFile, resolveOrientation,
} from './nidToPdfEngine';
import OutputFormatSelect from '../shared/OutputFormatSelect';
import { PAGE_OUTPUT_FORMATS } from '../shared/imageOutputFormats';
import { nidMaxCopies } from './nidCardConstants';
import { autoCropCardFile } from './detectCardQuad';
import { loadPreparedObjectUrl, prepareImageFile } from '../crop/cropImageEngine';
import {
    CLEAN_PRESETS,
    DEFAULT_ADJUSTMENTS,
    imageDataToBlob,
    imageToImageData,
    loadDocumentImage,
    processDocument,
    slidersForPreset,
} from '../document-cleanup/documentCleanupEngine';
import { downloadBlob } from '../../toolPageUtils';

const MAX_FILE_BYTES = 500 * 1024 * 1024;
const ACCEPT = 'image/*,.jpg,.jpeg,.png,.webp,.avif,.gif,.bmp,.tif,.tiff,.heic,.heif';

const ORIENTATION_OPTIONS = [
    { id: 'auto', labelKey: 'nidToPdf.orientation.auto', Icon: IconWand },
    { id: 'portrait', labelKey: 'nidToPdf.orientation.portrait', Icon: IconRectangleVertical },
    { id: 'landscape', labelKey: 'nidToPdf.orientation.landscape', Icon: IconRectangle },
];

const POSITION_OPTIONS = [
    { id: 'top', labelKey: 'nidToPdf.position.top', Icon: IconLayoutAlignTop },
    { id: 'center', labelKey: 'nidToPdf.position.center', Icon: IconLayoutAlignMiddle },
    { id: 'bottom', labelKey: 'nidToPdf.position.bottom', Icon: IconLayoutAlignBottom },
];

const COPY_OPTIONS = [1, 2, 3, 4];

const DEFAULT_SETTINGS = {
    layout: 'sideBySide',
    orientation: 'portrait',
    vPosition: 'top',
    roundedCorners: false,
    // The card photo is cleaned up with the Document Cleanup engine before it
    // reaches the page, so the shared PDF engine's own brightness/contrast/
    // sharpen stage is pinned to a no-op.
    brightness: 100,
    contrast: 100,
    sharpen: 0,
    cleanPreset: 'none',
    cleanAdjustments: { ...DEFAULT_ADJUSTMENTS },
    copies: 1,
    rotation: 0,
    // Per-face turn, on top of `rotation` — fixes one sideways/upside-down upload
    // without spinning the whole page.
    rotationFront: 0,
    rotationBack: 0,
};

const ROTATION_OPTIONS = [0, 90, 180, 270];

// Same tray as Document Cleanup — the presets are that tool's, so they get its
// glyphs too rather than a second, text-only look for the same four choices.
const CLEAN_PRESET_ICONS = {
    normal: IconWand,
    colorClean: IconSparkles,
    colorScan: IconPalette,
    bw: IconContrast,
};

function presetBtnClass(active) {
    return `flex min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all disabled:cursor-not-allowed disabled:opacity-45 ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

/** Run the Document Cleanup pipeline over one card photo. */
async function cleanCardFile(file, preset, adjustments) {
    const img = await loadDocumentImage(file);
    const cleaned = processDocument(imageToImageData(img), { preset, adjustments });
    const blob = await imageDataToBlob(cleaned, 'image/jpeg', 0.95);
    return new File([blob], file.name, { type: 'image/jpeg' });
}

function segBtnClass(active) {
    return `flex-1 min-h-[34px] rounded-lg border-none px-2 py-1.5 text-[12.5px] transition-all ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.06)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

/* Segmented control container */
function Segmented({ children }) {
    return (
        <div className="flex gap-1 rounded-[11px] border border-[#E8E8EE] bg-[#F7F7F9] p-1 dark:border-zinc-700 dark:bg-zinc-800">
            {children}
        </div>
    );
}

/* Little visual layout icon (two rects, side-by-side or stacked) */
function LayoutGlyph({ row, active }) {
    const color = active ? '#F77B1C' : '#B4B0AA';
    return (
        <span
            className="flex items-center justify-center gap-[3px] rounded"
            style={{ width: 34, height: 22, flexDirection: row ? 'row' : 'column' }}
        >
            <span style={{ background: color, borderRadius: 2, width: row ? 13 : 22, height: row ? 18 : 8 }} />
            <span style={{ background: color, borderRadius: 2, width: row ? 13 : 22, height: row ? 18 : 8 }} />
        </span>
    );
}

/**
 * Card outline turned by `deg`. The photo block in one corner is what tells 0°
 * from 180° — a bare rectangle would look identical at both.
 */
function RotationGlyph({ deg, active }) {
    const color = active ? '#F77B1C' : '#B4B0AA';
    return (
        <span className="flex h-[22px] w-[34px] items-center justify-center">
            <svg width="26" height="26" viewBox="0 0 26 26" aria-hidden="true" style={{ transform: `rotate(${deg}deg)` }}>
                <rect x="2.5" y="6.5" width="21" height="13" rx="2" fill="none" stroke={color} strokeWidth="1.8" />
                <rect x="5" y="9" width="6" height="8" rx="1" fill={color} />
                <path d="M13.5 10.5h7M13.5 13.5h7M13.5 16.5h4.5" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
            </svg>
        </span>
    );
}

/* Adjustment slider row with a colored value pill */
function AdjustSlider({ label, value, onChange, min, max, step = 1, suffix = '', pillClass, accentClass }) {
    return (
        <div className="space-y-1.5">
            <div className="flex items-center justify-between">
                <span className="text-[12px] font-medium text-[#3A3A42] dark:text-zinc-300">{label}</span>
                <span className={`rounded px-1.5 py-0.5 text-[11px] font-bold tabular-nums ${pillClass}`}>
                    {toBnDigits(value)}{suffix}
                </span>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className={`w-full ${accentClass}`}
            />
        </div>
    );
}

/** Build a PDF blob from the current files/settings and print it via a hidden iframe. */
function printBlob(blob) {
    const url = URL.createObjectURL(blob);
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.src = url;
    const cleanup = () => {
        setTimeout(() => {
            URL.revokeObjectURL(url);
            iframe.remove();
        }, 1000);
    };
    iframe.onload = () => {
        try {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
        } catch { /* ignore */ }
        window.addEventListener('focus', cleanup, { once: true });
        setTimeout(cleanup, 120000);
    };
    document.body.appendChild(iframe);
}

export default function NidToPdfImage() {
    const { t } = useTranslation();
    const frontInputRef = useRef(null);
    const backInputRef = useRef(null);

    const [frontFile, setFrontFile] = useState(null);
    const [backFile, setBackFile] = useState(null);
    const [frontSourceFile, setFrontSourceFile] = useState(null);
    const [backSourceFile, setBackSourceFile] = useState(null);
    // Corners last applied per slot, in source-image coordinates. Reopening the
    // crop reuses them instead of re-running detection from scratch.
    const [frontQuad, setFrontQuad] = useState(null);
    const [backQuad, setBackQuad] = useState(null);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    // Left-column tabs: 'settings' = the tool controls, 'booth' = the photo-booth
    // queue (tab exists only while the booth is ON; picks fill the two card slots —
    // the auto-crop side classifier may still reroute front/back).
    const [sideTab, setSideTab] = useState('settings');
    const [boothOn, setBoothOn] = useState(false);
    // Booth photos already loaded into the card slots — stay checked on the booth
    // tab; reset once both slots are empty again (e.g. after রিসেট).
    const [boothMarkedIds, setBoothMarkedIds] = useState([]);
    useEffect(() => { if (!frontFile && !backFile) setBoothMarkedIds([]); }, [frontFile, backFile]);
    const [draggingSlot, setDraggingSlot] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [result, setResult] = useState(null);
    // What the run button hands over: the PDF, or the same sheet as a picture.
    // Printing always goes through the PDF — that is the sheet a printer lays out.
    const [outputFormat, setOutputFormat] = useState('pdf');
    const [cropModal, setCropModal] = useState(null);
    const [cropBusy, setCropBusy] = useState(false);
    const [autoCrop, setAutoCrop] = useState(true);
    const [detectingSlot, setDetectingSlot] = useState(null);

    const updateSettings = useCallback((patch) => {
        setSettings((prev) => ({ ...prev, ...patch }));
        setResult(null);
    }, []);

    const effectiveOrientation = useMemo(
        () => resolveOrientation(settings.layout, settings.orientation, settings.copies),
        [settings.layout, settings.orientation, settings.copies],
    );

    const portraitOnly = settings.copies > 2;
    const maxCopies = nidMaxCopies(settings.rotation);

    useEffect(() => {
        if (portraitOnly && settings.orientation !== 'portrait') {
            updateSettings({ orientation: 'portrait' });
        }
    }, [portraitOnly, settings.orientation, updateSettings]);

    // Turning the cards drops the ceiling to 3, so a page already set to 4 steps down.
    useEffect(() => {
        if (settings.copies > maxCopies) updateSettings({ copies: maxCopies });
    }, [maxCopies, settings.copies, updateSettings]);

    const visibleOrientationOptions = useMemo(
        () => ORIENTATION_OPTIONS.filter((opt) => (portraitOnly ? opt.id === 'portrait' : true)),
        [portraitOnly],
    );

    const previewSettings = useMemo(
        () => ({ ...settings, orientation: effectiveOrientation }),
        [settings, effectiveOrientation],
    );

    // ── Document cleanup (same engine as the Document Cleanup tool) ──
    const cleanPreset = settings.cleanPreset || 'none';
    const cleanAdjustments = settings.cleanAdjustments || DEFAULT_ADJUSTMENTS;
    const cleanActive = cleanPreset !== 'none'
        || Object.values(cleanAdjustments).some((v) => Number(v) > 0);
    const visibleSliders = useMemo(() => slidersForPreset(cleanPreset), [cleanPreset]);

    const resetCleanup = () => updateSettings({
        cleanPreset: 'none',
        cleanAdjustments: { ...DEFAULT_ADJUSTMENTS },
    });
    const setCleanSlider = (key, value) => updateSettings({
        cleanAdjustments: { ...cleanAdjustments, [key]: value },
    });

    const [cleanFront, setCleanFront] = useState(null);
    const [cleanBack, setCleanBack] = useState(null);
    const [cleaning, setCleaning] = useState(false);

    // Cleaning is a full pixel pass, so it runs once per settled change and its
    // result is reused by both the live preview and the PDF — they can never
    // disagree about what the card looks like.
    useEffect(() => {
        if (!cleanActive || (!frontFile && !backFile)) {
            setCleanFront(null);
            setCleanBack(null);
            setCleaning(false);
            return undefined;
        }
        let cancelled = false;
        setCleaning(true);
        const timer = setTimeout(() => {
            void (async () => {
                try {
                    const [f, b] = await Promise.all([
                        frontFile ? cleanCardFile(frontFile, cleanPreset, cleanAdjustments) : null,
                        backFile ? cleanCardFile(backFile, cleanPreset, cleanAdjustments) : null,
                    ]);
                    if (cancelled) return;
                    setCleanFront(f);
                    setCleanBack(b);
                    setResult(null);
                } catch {
                    if (!cancelled) {
                        setCleanFront(null);
                        setCleanBack(null);
                    }
                } finally {
                    if (!cancelled) setCleaning(false);
                }
            })();
        }, 150);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [frontFile, backFile, cleanPreset, cleanAdjustments, cleanActive]);

    // Until a cleaned copy lands, the original keeps the preview from blinking.
    const renderFront = (cleanActive && cleanFront) || frontFile;
    const renderBack = (cleanActive && cleanBack) || backFile;

    const pickFile = useCallback((incoming) => {
        const file = Array.from(incoming || [])[0];
        if (!file) return null;
        if (!isSupportedImageFile(file)) {
            notifications.show({ title: t('common.error'), message: t('nidToPdf.errors.invalidType'), color: 'red' });
            return null;
        }
        if (file.size > MAX_FILE_BYTES) {
            notifications.show({ title: t('common.error'), message: t('nidToPdf.errors.tooLarge'), color: 'red' });
            return null;
        }
        return file;
    }, [t]);

    /**
     * What the detector put in each slot, and how sure it was. Only auto-placed
     * photos are listed, so a later photo can take a slot back off a shakier
     * reading without ever moving something the user placed themselves.
     */
    const autoPlaced = useRef({ front: null, back: null });

    const assignFile = useCallback((slot, file) => {
        if (!file) return;
        if (slot === 'front') setFrontFile(file);
        else setBackFile(file);
        setResult(null);
    }, []);

    const closeCropModal = useCallback(() => {
        setCropModal((prev) => {
            if (prev?.src) URL.revokeObjectURL(prev.src);
            return null;
        });
        setCropBusy(false);
    }, []);

    const openCropModal = useCallback(async (slot, file, sourceFile = file) => {
        try {
            const src = await loadPreparedObjectUrl(file);
            setCropModal({ slot, file, sourceFile, src });
        } catch {
            notifications.show({ title: t('common.error'), message: t('nidToPdf.crop.loadFailed'), color: 'red' });
        }
    }, [t]);

    const handleCropApply = useCallback(async (blob, quad) => {
        if (!cropModal) return;
        setCropBusy(true);
        try {
            const base = cropModal.sourceFile.name.replace(/\.[^.]+$/, '');
            const cropped = new File([blob], `${base}-cropped.jpg`, { type: 'image/jpeg' });
            assignFile(cropModal.slot, cropped);
            // Cropped by hand: the detector no longer owns this slot.
            autoPlaced.current[cropModal.slot] = null;
            if (cropModal.slot === 'front') {
                setFrontSourceFile(cropModal.sourceFile);
                setFrontQuad(quad || null);
            } else {
                setBackSourceFile(cropModal.sourceFile);
                setBackQuad(quad || null);
            }
            closeCropModal();
        } finally {
            setCropBusy(false);
        }
    }, [assignFile, closeCropModal, cropModal]);

    /**
     * Upload → corner detection → flat CR80 crop → front/back recognition, then
     * the card lands in the slot it actually belongs to (a front photo dropped on
     * the Back box still fills Front, as long as Front is free).
     * The crop modal only opens when auto-crop is switched off; a failed detection
     * keeps the original photo so the manual crop button can still fix it.
     * `taken` (booth batch loads): slots already filled EARLIER IN THE SAME BATCH —
     * state is stale inside one handler run, so without it a second photo could
     * reroute onto (and overwrite) the slot the first one just landed in.
     * Returns the slot the photo actually landed in (null when nothing landed).
     */
    const handleFileInput = useCallback(async (slot, files, { taken } = {}) => {
        const picked = pickFile(files);
        if (!picked) return null;
        setDetectingSlot(slot);
        try {
            let prepared = picked;
            try {
                prepared = await prepareImageFile(picked);
            } catch {
                prepared = picked;
            }
            if (!autoCrop) {
                await openCropModal(slot, prepared);
                return slot;
            }
            const { file, detected, side, sideConfidence } = await autoCropCardFile(prepared);

            // The photo itself says which face of the card it is. Send it to that
            // slot when the slot is free — dropping a front photo on the "back"
            // box should still fill Front. An occupied slot is never overwritten.
            const frontBusy = frontFile || taken?.has('front');
            const backBusy = backFile || taken?.has('back');
            let target = slot;
            if (side && side !== slot && !(side === 'front' ? frontBusy : backBusy)) {
                target = side;
            }

            // Two photos of the same card cannot both be the front. When this one
            // claims the face an EARLIER AUTO-PLACED photo is sitting in, and says
            // so more confidently, the pair is re-read as a pair: the less certain
            // photo moves across rather than the more certain one being turned
            // away. A photo the user cropped or placed by hand is never moved.
            const held = side ? autoPlaced.current[side] : null;
            if (side && held && sideConfidence > held.confidence + 0.05) {
                const other = side === 'front' ? 'back' : 'front';
                if (!(other === 'front' ? frontBusy : backBusy) || target === other) {
                    assignFile(other, held.file);
                    autoPlaced.current[other] = { ...held };
                    autoPlaced.current[side] = null;
                    target = side;
                }
            }
            assignFile(target, file);
            autoPlaced.current[target] = side === target ? { file, confidence: sideConfidence ?? 0 } : null;
            // A fresh photo starts unturned; the old face's manual rotate does not carry over.
            updateSettings(target === 'front' ? { rotationFront: 0 } : { rotationBack: 0 });
            if (target === 'front') { setFrontSourceFile(prepared); setFrontQuad(null); }
            else { setBackSourceFile(prepared); setBackQuad(null); }

            const sideLabel = t(side === 'front' ? 'nidToPdf.autoCrop.sideFront' : 'nidToPdf.autoCrop.sideBack');
            if (target !== slot) {
                notifications.show({
                    title: t('nidToPdf.autoCrop.title'),
                    message: t('nidToPdf.autoCrop.sidePlaced', { side: sideLabel }),
                    color: 'blue',
                });
            } else if (side && side !== slot) {
                notifications.show({
                    title: t('nidToPdf.autoCrop.title'),
                    message: t('nidToPdf.autoCrop.sideMismatch', { side: sideLabel }),
                    color: 'yellow',
                });
            }
            if (!detected) {
                notifications.show({
                    title: t('nidToPdf.autoCrop.title'),
                    message: t('nidToPdf.autoCrop.failed'),
                    color: 'yellow',
                });
            }
            return target;
        } finally {
            setDetectingSlot(null);
        }
    }, [assignFile, autoCrop, backFile, frontFile, openCropModal, pickFile, updateSettings, t]);

    const openPicker = (slot) => {
        if (slot === 'front') frontInputRef.current?.click();
        else backInputRef.current?.click();
    };

    // ── Preview slot handlers ──
    const onDrop = (slot, e) => {
        e.preventDefault();
        setDraggingSlot(null);
        void handleFileInput(slot, e.dataTransfer.files);
    };
    const onDragOver = (slot, e) => { e.preventDefault(); setDraggingSlot(slot); };
    const onDragLeave = () => setDraggingSlot(null);
    const onCropSlot = (slot) => {
        const src = slot === 'front' ? (frontSourceFile || frontFile) : (backSourceFile || backFile);
        if (src) void openCropModal(slot, src, src);
    };
    const onRemoveSlot = (slot) => {
        autoPlaced.current[slot] = null;
        if (slot === 'front') { setFrontFile(null); setFrontSourceFile(null); setFrontQuad(null); }
        else { setBackFile(null); setBackSourceFile(null); setBackQuad(null); }
        updateSettings(slot === 'front' ? { rotationFront: 0 } : { rotationBack: 0 });
        setResult(null);
    };
    const onSwap = () => {
        // The user has just overruled the detector; it must not overrule back.
        autoPlaced.current = { front: null, back: null };
        setFrontFile(backFile);
        setBackFile(frontFile);
        setFrontSourceFile(backSourceFile);
        setBackSourceFile(frontSourceFile);
        // Corners belong to the photo, so they follow it across the swap.
        setFrontQuad(backQuad);
        setBackQuad(frontQuad);
        // The turn belongs to the photo, not the slot, so it rides along with it.
        updateSettings({ rotationFront: settings.rotationBack, rotationBack: settings.rotationFront });
        setResult(null);
    };
    const onDuplicate = () => {
        if (settings.copies >= maxCopies) return;
        updateSettings({ copies: settings.copies + 1 });
    };
    const onRotate = () => updateSettings({ rotation: ((settings.rotation || 0) + 90) % 360 });
    const onRotateSlot = (slot) => {
        const key = slot === 'front' ? 'rotationFront' : 'rotationBack';
        updateSettings({ [key]: ((settings[key] || 0) + 90) % 360 });
    };

    // Booth multi-pick (bulk-edit style): the FIRST TWO chosen photos land in the two
    // card slots — front first, then back, tracked batch-locally so the side
    // classifier can't overwrite a slot filled a moment ago. Extra selections are
    // ignored. Afterwards the column jumps back to the সেটিংস tab (the loaded cards
    // are visible in the preview beside it). With অটো ক্রপ off (manual crop modal,
    // one at a time) only the first photo is taken.
    const handleBoothPickMany = useCallback(async (files, ids) => {
        const taken = new Set();
        if (frontFile) taken.add('front');
        if (backFile) taken.add('back');
        const take = autoCrop ? files.slice(0, Math.max(1, 2 - taken.size)) : files.slice(0, 1);
        for (const f of take) {
            const slot = !taken.has('front') ? 'front' : 'back';
            const landed = await handleFileInput(slot, [f], { taken }); // eslint-disable-line no-await-in-loop
            taken.add(landed || slot);
        }
        setBoothMarkedIds(prev => [...prev, ...ids.slice(0, take.length)]);
        setSideTab('settings');
    }, [frontFile, backFile, autoCrop, handleFileInput]);

    // Ctrl/Cmd+V anywhere on the page drops copied cards into the empty slots —
    // front first, then back, exactly like a booth pick. Ignored once both slots
    // are full, so a stray paste never overwrites a card already placed.
    const handlePasteFiles = useCallback(async (files) => {
        const taken = new Set();
        if (frontFile) taken.add('front');
        if (backFile) taken.add('back');
        if (taken.size === 2) return;
        // With অটো ক্রপ off each photo opens the crop modal, so only one is taken.
        const take = autoCrop ? files.slice(0, 2 - taken.size) : files.slice(0, 1);
        for (const f of take) {
            const slot = !taken.has('front') ? 'front' : 'back';
            const landed = await handleFileInput(slot, [f], { taken }); // eslint-disable-line no-await-in-loop
            taken.add(landed || slot);
        }
    }, [frontFile, backFile, autoCrop, handleFileInput]);

    useImagePaste(
        useCallback((files) => { void handlePasteFiles(files); }, [handlePasteFiles]),
        { enabled: !processing && !cleaning && !cropModal },
    );

    const handleReset = () => {
        autoPlaced.current = { front: null, back: null };
        setFrontFile(null);
        setBackFile(null);
        setFrontSourceFile(null);
        setBackSourceFile(null);
        setFrontQuad(null);
        setBackQuad(null);
        setSettings(DEFAULT_SETTINGS);
        setResult(null);
    };

    const canRun = Boolean(frontFile && backFile);

    const buildPdf = useCallback(async () => {
        if (result) return result;
        const blob = await createNidPdf(renderFront, renderBack, previewSettings);
        setResult(blob);
        return blob;
    }, [result, renderFront, renderBack, previewSettings]);

    const handleCreate = async () => {
        if (!canRun || processing) return;
        recordToolUsage('image/nid-to-pdf', { category: 'image' });
        setProcessing(true);
        try {
            if (outputFormat === 'pdf') {
                const blob = await buildPdf();
                downloadBlob(blob, 'nid.pdf');
            } else {
                // `ext` is what the file REALLY is: a browser that cannot write the
                // chosen format falls back inside encodeCanvas, and naming the file
                // after the fallback keeps it openable.
                const out = await createNidPageImage(renderFront, renderBack, previewSettings, outputFormat);
                downloadBlob(out.blob, `nid.${out.ext}`);
            }
            notifications.show({ title: t('common.success'), message: t('nidToPdf.done'), color: 'green' });
        } catch {
            notifications.show({ title: t('common.error'), message: t('nidToPdf.errors.failed'), color: 'red' });
        } finally {
            setProcessing(false);
        }
    };

    const handlePrint = async () => {
        if (!canRun || printing) return;
        recordToolUsage('image/nid-to-pdf', { category: 'image' });
        setPrinting(true);
        try {
            const blob = await buildPdf();
            printBlob(blob);
        } catch {
            notifications.show({ title: t('common.error'), message: t('nidToPdf.errors.failed'), color: 'red' });
        } finally {
            setPrinting(false);
        }
    };

    const status = canRun ? 'ready' : 'empty';
    // The line reads out whatever the output picker is set to, so it never promises
    // a PDF while the button says "AVIF ডাউনলোড করুন".
    const outputLabel = outputFormat === 'pdf'
        ? 'PDF'
        : t(`toolPage.outputFormat.formats.${outputFormat}`);
    const statusText = canRun
        ? t('nidToPdf.readyHint', { format: outputLabel })
        : t('nidToPdf.previewEmpty');

    const cropModalTitle = cropModal?.slot === 'front'
        ? t('nidToPdf.frontTitle')
        : cropModal?.slot === 'back' ? t('nidToPdf.backTitle') : '';

    const copiesLabel = settings.copies > 1
        ? t('nidToPdf.copiesFooter', { count: toBnDigits(settings.copies) })
        : t('nidToPdf.copiesFooterOne');

    return (
        <>
        <ToolPageLayout
            slug="image/nid-to-pdf"
            subtitleKey="nidToPdf.subtitle"
            status={status}
            statusText={statusText}
            // The সেটিংস/ফটো বুথ tab bar carries its own labels — the card header would
            // duplicate "সেটিংস" above it, so it only shows while the booth is OFF.
            controlsTitle={boothOn ? undefined : t('nidToPdf.settingsTitle')}
            stickyPreview
            showTrust={false}
            onReset={frontFile || backFile ? handleReset : undefined}
            primaryAction={{
                label: outputFormat === 'pdf'
                    ? t('nidToPdf.create')
                    : t('toolPage.downloadAs', { format: t(`toolPage.outputFormat.formats.${outputFormat}`) }),
                onClick: handleCreate,
                disabled: !canRun || processing || cleaning,
                loading: processing || cleaning,
            }}
            // Print belongs with the run button, not buried at the foot of the
            // settings column — it is the other thing you do with a finished sheet.
            secondaryAction={{
                key: 'print',
                label: printing ? t('nidToPdf.printing') : t('nidToPdf.print'),
                icon: IconPrinter,
                onClick: handlePrint,
                disabled: !canRun || printing || cleaning,
            }}
            actionExtra={(
                <OutputFormatSelect
                    formats={PAGE_OUTPUT_FORMATS}
                    value={outputFormat}
                    onChange={setOutputFormat}
                    disabled={processing || cleaning}
                />
            )}
            controls={(
                <div className="space-y-4">
                    <input ref={frontInputRef} type="file" accept={ACCEPT} className="hidden"
                        onChange={(e) => { void handleFileInput('front', e.target.files); e.target.value = ''; }} />
                    <input ref={backInputRef} type="file" accept={ACCEPT} className="hidden"
                        onChange={(e) => { void handleFileInput('back', e.target.files); e.target.value = ''; }} />

                    {/* সেটিংস / ফটো বুথ tabs — booth tab only while the booth is ON. A booth
                        pick lands straight in the live preview (front first, then back). */}
                    {boothOn && (
                        <div className="grid grid-cols-2 gap-1.5 p-1 rounded-lg bg-zinc-100 dark:bg-zinc-800">
                            {[
                                { id: 'settings', label: t('nidToPdf.settingsTitle') },
                                { id: 'booth', label: t('photoCollectorPanel.title') },
                            ].map((tb) => (
                                <button key={tb.id} type="button" onClick={() => setSideTab(tb.id)}
                                    className={`py-1.5 rounded-md text-xs font-semibold transition-colors ${sideTab === tb.id ? 'bg-white dark:bg-zinc-900 text-primary shadow-sm' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'}`}>
                                    {tb.label}
                                </button>
                            ))}
                        </div>
                    )}
                    {/* Booth pane — kept mounted (hidden) so the on/off status resolves.
                        Multi-select: taps choose up to two photos, সিলেক্ট loads them into the
                        front/back slots; already-loaded photos stay checked. */}
                    <div className={boothOn && sideTab === 'booth' ? undefined : 'hidden'}>
                        <PhotoBoothInbox multiSelect markedIds={boothMarkedIds} onPickFiles={handleBoothPickMany} onStatusChange={setBoothOn} />
                    </div>
                    <div className={boothOn && sideTab === 'booth' ? 'hidden' : 'space-y-4'}>
                    {/* ── Auto crop (client-side corner detection on upload) ── */}
                    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-[#ECE8E4] bg-[#FFF9F2] px-3 py-2.5 dark:border-zinc-700 dark:bg-zinc-800/60">
                        <span className="min-w-0 text-[12.5px] font-semibold text-[#1B1B1F] dark:text-zinc-200">
                            {t('nidToPdf.autoCrop.title')}
                        </span>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={autoCrop}
                            onClick={() => setAutoCrop((v) => !v)}
                            className={`relative h-[22px] w-10 flex-none rounded-full transition-colors ${autoCrop ? 'bg-primary' : 'bg-[#D6D6DE] dark:bg-zinc-600'}`}
                        >
                            <span className={`absolute top-[3px] block h-4 w-4 rounded-full bg-white shadow transition-[left] ${autoCrop ? 'left-[21px]' : 'left-[3px]'}`} />
                        </button>
                    </label>

                    {/* ── Page settings ── */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-[12.5px] font-semibold text-[#1B1B1F] dark:text-zinc-200">{t('nidToPdf.pageTitle')}</span>
                            <span className="text-[11px] font-medium text-[#9A9AA3] dark:text-zinc-500">{t('nidToPdf.pageLabel')}</span>
                        </div>
                        <Segmented>
                            {/* Icon only; the name lives in the tooltip and the
                                accessible label so the row stays compact. */}
                            {visibleOrientationOptions.map((opt) => (
                                <Hint label={t(opt.labelKey)} key={opt.id}>
                                    <button
                                        type="button"
                                        onClick={() => updateSettings({ orientation: opt.id })}
                                        aria-label={t(opt.labelKey)}
                                        className={`${segBtnClass(settings.orientation === opt.id)} flex items-center justify-center`}
                                    >
                                        <opt.Icon size={18} stroke={1.9} />
                                    </button>
                                </Hint>
                            ))}
                        </Segmented>
                        {portraitOnly ? (
                            <p className="text-[11px] text-[#A8A099] dark:text-zinc-500">{t('nidToPdf.portraitOnlyHint')}</p>
                        ) : null}
                    </div>

                    {/* ── Layout ── */}
                    <div className="space-y-2 border-t border-[#F0EEEB] pt-3.5 dark:border-zinc-800">
                        <div className="flex items-center justify-between">
                            <span className="text-[12.5px] font-semibold text-[#1B1B1F] dark:text-zinc-200">{t('nidToPdf.layoutTitle')}</span>
                            <button
                                type="button"
                                onClick={() => updateSettings({ layout: settings.layout === 'sideBySide' ? 'stacked' : 'sideBySide' })}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary"
                            >
                                <IconRefresh size={12} stroke={2} />
                                {t('nidToPdf.layoutReset')}
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {[{ id: 'sideBySide', row: true }, { id: 'stacked', row: false }].map((opt) => {
                                const active = settings.layout === opt.id;
                                return (
                                    <Hint label={t(`nidToPdf.layout.${opt.id}`)} key={opt.id}>
                                        <button
                                            type="button"
                                            onClick={() => updateSettings({ layout: opt.id })}
                                            aria-label={t(`nidToPdf.layout.${opt.id}`)}
                                            className={`flex items-center justify-center rounded-xl border px-2 py-2.5 transition-colors ${
                                                active
                                                    ? 'border-primary bg-[#FFF4E6] dark:border-primary dark:bg-primary/10'
                                                    : 'border-[#ECE8E4] bg-white hover:border-primary/40 dark:border-zinc-700 dark:bg-zinc-900'
                                            }`}
                                        >
                                            <LayoutGlyph row={opt.row} active={active} />
                                        </button>
                                    </Hint>
                                );
                            })}
                        </div>
                    </div>

                    {/* ── Rotate (applies to BOTH cards together) ── */}
                    <div className="space-y-2 border-t border-[#F0EEEB] pt-3.5 dark:border-zinc-800">
                        <div className="flex items-center justify-between">
                            <span className="text-[12.5px] font-semibold text-[#1B1B1F] dark:text-zinc-200">{t('nidToPdf.rotateTitle')}</span>
                            <button
                                type="button"
                                onClick={() => updateSettings({ rotation: (settings.rotation + 90) % 360 })}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary"
                            >
                                <IconRotateClockwise size={12} stroke={2} />
                                {t('nidToPdf.rotate90')}
                            </button>
                        </div>
                        <Segmented>
                            {ROTATION_OPTIONS.map((deg) => (
                                <Hint label={`${toBnDigits(deg)}°`} key={deg}>
                                    <button
                                        type="button"
                                        onClick={() => updateSettings({ rotation: deg })}
                                        aria-label={`${toBnDigits(deg)}°`}
                                        className={`${segBtnClass(settings.rotation === deg)} flex items-center justify-center`}
                                    >
                                        <RotationGlyph deg={deg} active={settings.rotation === deg} />
                                    </button>
                                </Hint>
                            ))}
                        </Segmented>
                        <p className="text-[11px] text-[#A8A099] dark:text-zinc-500">{t('nidToPdf.rotateHint')}</p>
                    </div>

                    {/* ── Image position (single copy — top / center / bottom on the page) ── */}
                    {settings.copies === 1 ? (
                        <div className="space-y-2 border-t border-[#F0EEEB] pt-3.5 dark:border-zinc-800">
                            <span className="text-[12.5px] font-semibold text-[#1B1B1F] dark:text-zinc-200">{t('nidToPdf.positionTitle')}</span>
                            <Segmented>
                                {POSITION_OPTIONS.map((opt) => (
                                    <Hint label={t(opt.labelKey)} key={opt.id}>
                                        <button
                                            type="button"
                                            onClick={() => updateSettings({ vPosition: opt.id })}
                                            aria-label={t(opt.labelKey)}
                                            className={`${segBtnClass(settings.vPosition === opt.id)} flex items-center justify-center`}
                                        >
                                            <opt.Icon size={18} stroke={1.9} />
                                        </button>
                                    </Hint>
                                ))}
                            </Segmented>
                        </div>
                    ) : null}

                    {/* ── Copies ── */}
                    <div className="space-y-2 border-t border-[#F0EEEB] pt-3.5 dark:border-zinc-800">
                        <span className="text-[12.5px] font-semibold text-[#1B1B1F] dark:text-zinc-200">{t('nidToPdf.copiesTitle')}</span>
                        <Segmented>
                            {COPY_OPTIONS.filter((count) => count <= maxCopies).map((count) => (
                                <button key={count} type="button" onClick={() => updateSettings({ copies: count })} className={segBtnClass(settings.copies === count)}>
                                    {toBnDigits(count)}
                                </button>
                            ))}
                        </Segmented>
                        {maxCopies < COPY_OPTIONS.length && (
                            <p className="text-[11px] text-[#A8A099] dark:text-zinc-500">{t('nidToPdf.copiesRotatedHint')}</p>
                        )}
                    </div>

                    {/* ── Rounded corners ── */}
                    <label className="flex cursor-pointer items-center justify-between gap-3 border-t border-[#F0EEEB] pt-3.5 dark:border-zinc-800">
                        <span>
                            <span className="block text-[12.5px] font-semibold text-[#1B1B1F] dark:text-zinc-200">{t('nidToPdf.roundedCorners')}</span>
                            <span className="mt-0.5 block text-[10px] font-normal text-[#9A9AA3] dark:text-zinc-500">{t('nidToPdf.roundedCornersHint')}</span>
                        </span>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={settings.roundedCorners}
                            onClick={() => updateSettings({ roundedCorners: !settings.roundedCorners })}
                            className={`relative h-[22px] w-10 flex-none rounded-full transition-colors ${settings.roundedCorners ? 'bg-primary' : 'bg-[#D6D6DE] dark:bg-zinc-600'}`}
                        >
                            <span className={`absolute top-[3px] block h-4 w-4 rounded-full bg-white shadow transition-[left] ${settings.roundedCorners ? 'left-[21px]' : 'left-[3px]'}`} />
                        </button>
                    </label>

                    {/* ── Clean up (Document Cleanup engine, run on the card photo) ── */}
                    <div className="space-y-3 border-t border-[#F0EEEB] pt-3.5 dark:border-zinc-800">
                        <div className="flex items-center justify-between">
                            <span className="text-[12.5px] font-semibold text-[#1B1B1F] dark:text-zinc-200">
                                {t('documentCleanupImage.presetTitle')}
                            </span>
                            <button type="button" onClick={resetCleanup} disabled={!cleanActive}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-primary transition-opacity disabled:cursor-default disabled:opacity-40">
                                <IconRestore size={12} stroke={2} />
                                {t('nidToPdf.resetAdjustments')}
                            </button>
                        </div>
                        {/* Icons only — the name is in the tooltip (and read out via
                            aria-label), so all four fit on one row. */}
                        <div className="grid grid-cols-4 gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                            {CLEAN_PRESETS.map((id) => {
                                const Icon = CLEAN_PRESET_ICONS[id] || IconWand;
                                const label = t(`documentCleanupImage.presets.${id}`);
                                return (
                                    <Hint label={label} key={id}>
                                        <button
                                            type="button"
                                            onClick={() => updateSettings({ cleanPreset: cleanPreset === id ? 'none' : id })}
                                            className={presetBtnClass(cleanPreset === id)}
                                            aria-label={label}
                                            aria-pressed={cleanPreset === id}
                                        >
                                            <Icon size={20} stroke={1.8} />
                                        </button>
                                    </Hint>
                                );
                            })}
                        </div>
                        <p className="text-[11px] leading-relaxed text-[#9A9AA3] dark:text-zinc-500">
                            {t(`documentCleanupImage.presetHints.${cleanPreset}`)}
                        </p>

                        <div className="space-y-3 border-t border-[#F0EEEB] pt-3 dark:border-zinc-800">
                            <span className="block text-[12.5px] font-semibold text-[#1B1B1F] dark:text-zinc-200">
                                {t('documentCleanupImage.adjustTitle')}
                            </span>
                            {visibleSliders.map((key) => (
                                <AdjustSlider
                                    key={key}
                                    label={t(`documentCleanupImage.sliders.${key}`)}
                                    value={cleanAdjustments[key] ?? 0}
                                    onChange={(v) => setCleanSlider(key, v)}
                                    min={0}
                                    max={100}
                                    pillClass="bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300"
                                    accentClass="accent-amber-500"
                                />
                            ))}
                        </div>
                    </div>

                    {/* ── Footer: page/copies info. Print moved to the action bar. ── */}
                    <div className="border-t border-[#F0EEEB] pt-3.5 dark:border-zinc-800">
                        <div className="flex items-center justify-between text-[11.5px] font-medium text-[#9A9AA3] dark:text-zinc-500">
                            <span>{t('nidToPdf.pageFooter')}</span>
                            <span>{copiesLabel}</span>
                        </div>
                    </div>
                    </div>
                </div>
            )}
            preview={(
                <NidLivePreview
                    frontFile={renderFront}
                    backFile={renderBack}
                    settings={previewSettings}
                    draggingSlot={draggingSlot}
                    busySlot={detectingSlot}
                    onPick={openPicker}
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onCrop={onCropSlot}
                    onRemove={onRemoveSlot}
                    onSwap={onSwap}
                    onDuplicate={onDuplicate}
                    onRotate={onRotate}

                    onRotateSlot={onRotateSlot}
                    pasteHint={frontFile && backFile ? undefined : t('nidToPdf.pasteHint')}
                    t={t}
                />
            )}
        />
        <NidCardCropModal
            opened={Boolean(cropModal)}
            onClose={closeCropModal}
            imageSrc={cropModal?.src ?? null}
            initialQuad={cropModal?.slot === 'front' ? frontQuad : backQuad}
            title={cropModalTitle}
            fileName={cropModal?.file?.name}
            onApply={handleCropApply}
            busy={cropBusy}
        />
        </>
    );
}
